import React, { FC, useCallback, useState } from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import CompensationInfoChart from 'buyside/components/presentation/CompensationIndexPage/CompensationCard/CompensationInfo/compensationInfoChart';
import Modal from 'buyside/components/presentation/Modal/modal';

import { formatCompensations } from 'buyside/util/formatNumber';
import { useIsMobile } from 'buyside/util/hooks/useIsMobile';

import { CompensationIndexListQuery } from 'interfaces/graphql-typings';

import styles from './compensationInfoStyles.scss';

const CompensationInfo: FC<NonNullable<
  NonNullable<
    NonNullable<NonNullable<CompensationIndexListQuery['compensationIndexList']>['edges']>[number]
  >['node']
>> = ({
  annualDiscrBonus,
  assetClass,
  baseSalary,
  buildPersonalTrackRecord,
  fundStrategy,
  industry,
  interestIncentiveFeesPerc,
  interestIncentiveFeesValue,
  interestMgmtFeesPerc,
  interestMgmtFeesValue,
  investInFund,
  marketPersonalTrackRecord,
  pieChartData,
  totalCashComp,
  totalCashDeferredComp,
  totalDeferredComp,
  vestingIncentiveFees,
  vestingMgmtFees
}) => {
  const isMobile = useIsMobile();

  const [isOpen, setIsOpen] = useState(false);

  const renderFundInfo = useCallback(
    () => (
      <table className={styles.fundInfo}>
        <tbody>
          <tr>
            <th colSpan={2}>Fund Information</th>
          </tr>
          <tr>
            <td>
              <label>Fund Strategy:</label>
            </td>
            <td>{fundStrategy}</td>
          </tr>
          <tr>
            <td>
              <label>Industry Focus:</label>
            </td>
            <td>{industry}</td>
          </tr>
          <tr>
            <td>
              <label>Asset Class Expertise:</label>
            </td>
            <td>{assetClass}</td>
          </tr>
        </tbody>
      </table>
    ),
    [assetClass, fundStrategy, industry]
  );

  const renderCompBreakdown = useCallback(
    () => (
      <table className={styles.compBreakdown}>
        <tbody>
          <tr>
            <th colSpan={6}>Compensation Breakdown</th>
          </tr>
          <tr>
            <td colSpan={5}>
              <label>Base Salary:</label>
            </td>
            <td>{formatCompensations(baseSalary)}</td>
          </tr>
          <tr>
            <td colSpan={5}>
              <label>Mgmt. Fees ({interestMgmtFeesPerc}%):</label>
            </td>
            <td>{formatCompensations(interestMgmtFeesValue)}</td>
          </tr>
          <tr>
            <td colSpan={5}>
              <label>Incentive Fees ({interestIncentiveFeesPerc}%):</label>
            </td>
            <td>{formatCompensations(interestIncentiveFeesValue)}</td>
          </tr>
          <tr>
            <td colSpan={5}>
              <label>Other Cash Bonus:</label>
            </td>
            <td>{formatCompensations(annualDiscrBonus)}</td>
          </tr>
          <tr>
            <td colSpan={5}>
              <label>Total Cash Compensation:</label>
            </td>
            <td>{formatCompensations(totalCashComp)}</td>
          </tr>
          <tr>
            <td colSpan={5}>
              <label>Deferred Compensation:</label>
            </td>
            <td>{formatCompensations(totalDeferredComp)}</td>
          </tr>
          <tr>
            <td colSpan={5}>
              <label>Total Cash + Deferred:</label>
            </td>
            <td>{formatCompensations(totalCashDeferredComp)}</td>
          </tr>
          <tr>
            <td colSpan={4}>
              <label>Vesting Period:</label>
            </td>
            <td>
              <label>Mgmt. Fees</label>
            </td>
            <td>
              <label>Incentive Fees</label>
            </td>
          </tr>
          <tr>
            <td colSpan={4} />
            <td>{vestingMgmtFees} years</td>
            <td>{vestingIncentiveFees} years</td>
          </tr>
        </tbody>
      </table>
    ),
    [
      annualDiscrBonus,
      baseSalary,
      interestIncentiveFeesPerc,
      interestIncentiveFeesValue,
      interestMgmtFeesPerc,
      interestMgmtFeesValue,
      totalCashComp,
      totalCashDeferredComp,
      totalDeferredComp,
      vestingIncentiveFees,
      vestingMgmtFees
    ]
  );

  const renderCapabilitiesBreakdown = useCallback(
    () => (
      <table className={styles.capabilitiesBreakdown}>
        <tbody>
          <tr>
            <th colSpan={6}>This Employee Can...</th>
          </tr>
          {investInFund ? (
            <tr>
              <td colSpan={6}>
                <label>
                  <FontAwesomeIcon icon={faCheck} /> Invest in his/her fund without paying fees
                </label>
              </td>
            </tr>
          ) : null}
          {buildPersonalTrackRecord ? (
            <tr>
              <td colSpan={6}>
                <label>
                  <FontAwesomeIcon icon={faCheck} /> Build a personal track record through managing
                  a book or sub-portfolio
                </label>
              </td>
            </tr>
          ) : null}
          {marketPersonalTrackRecord ? (
            <tr>
              <td colSpan={6}>
                <label>
                  <FontAwesomeIcon icon={faCheck} /> Market his/her personal track record
                  post-employment
                </label>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    ),
    [buildPersonalTrackRecord, investInFund, marketPersonalTrackRecord]
  );

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        onClick={() => setIsOpen(true)}
        size={isMobile ? BUTTON_SIZES.SMALL : BUTTON_SIZES.LARGE}
        text="More About This Compensation"
        type={BUTTON_TYPES.PRIMARY}
      />
    ),
    [isMobile]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      renderOpenModalButton={renderOpenModalButton}
      size="lg"
      title="Additional Details"
    >
      <div className={styles.compensationInfo}>
        <div>
          {renderFundInfo()}
          {renderCompBreakdown()}
          {buildPersonalTrackRecord || investInFund || marketPersonalTrackRecord
            ? renderCapabilitiesBreakdown()
            : null}
        </div>
        <CompensationInfoChart pieChartData={pieChartData} />
      </div>
    </Modal>
  );
};

export default CompensationInfo;
